import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  ListChecks as PhListChecks,
  Calendar as PhCalendar,
  Users as PhUsers,
  Archive as PhArchive,
  Buildings as PhBuildings,
  Gear as PhGear,
} from '@phosphor-icons/react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import { useAuth } from '../../App';
import WorkspaceSwitcher from '../organization/WorkspaceSwitcher';
import NotificationBell from '../notifications/NotificationBell';

/*
 * Shared layout used by stand-alone pages (e.g. ArchivesPage) that live
 * outside `Components.js`. Mirrors the inline `MobileLayout` defined in
 * Components.js — mobile keeps its gradient header + bottom nav while
 * desktop (lg+) gets the left sidebar and a clean white page header with
 * the WorkspaceSwitcher pinned to the top-right.
 */

const MobileLayout = ({
  children,
  title,
  subtitle,
  showHeader = true,
  showBack = false,
  onBack,
  rightAction,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLanguage();
  const { activeWorkspace } = useWorkspace();
  const { user } = useAuth();

  const inOrg = activeWorkspace?.kind === 'org';
  const isPro = user?.subscription_status === 'pro' || user?.subscription_status === 'trial' || user?.subscription_status === 'active';

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      navigate(-1);
    }
  };

  const navItems = [
    { to: '/',          Icon: PhListChecks, label: t('tasks'),    match: (p) => p === '/' },
    { to: '/calendar',  Icon: PhCalendar,   label: t('calendar'), match: (p) => p === '/calendar' },
    { to: '/contacts',  Icon: PhUsers,      label: t('contacts'), match: (p) => p === '/contacts' },
    { to: '/organization/members', Icon: PhBuildings, label: t('team') && t('team') !== 'team' ? t('team') : 'Team', match: (p) => p.startsWith('/organization') },
    { to: '/settings',  Icon: PhGear,       label: t('settings'), match: (p) => p.startsWith('/settings') },
  ];
  const itemsForSidebar = inOrg ? navItems : navItems.filter((item) => item.to !== '/organization/members');

  return (
    <div className="mobile-container min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="lg:flex lg:min-h-screen">
        {/* ---- Desktop sidebar (lg+ only) ---- */}
        <aside
          className={`hidden lg:flex lg:flex-col lg:min-h-screen lg:bg-white lg:border-r lg:border-gray-200 lg:px-4 lg:py-6 ${inOrg ? 'lg:w-64' : 'lg:w-56'}`}
          data-testid={inOrg ? 'business-desktop-sidebar' : 'personal-desktop-sidebar'}
        >
          <div className="flex items-center gap-2 mb-6 px-1">
            <img
              src="/logos/edotask-wordmark.svg"
              alt="Edotask"
              className="h-7"
              style={inOrg
                ? { filter: 'brightness(0) saturate(100%) invert(28%) sepia(94%) saturate(2008%) hue-rotate(229deg) brightness(95%) contrast(94%)' }
                : { filter: 'brightness(0) saturate(100%) invert(28%) sepia(94%) saturate(3151%) hue-rotate(207deg) brightness(95%) contrast(94%)' }
              }
            />
          </div>

          {inOrg && (
            <div className="mb-4 px-1">
              <WorkspaceSwitcher />
            </div>
          )}

          <nav className="flex flex-col gap-1">
            {itemsForSidebar.map((item) => {
              const active = item.match(location.pathname);
              const accentActive = inOrg ? 'bg-indigo-50 text-indigo-700' : 'bg-blue-50 text-blue-700';
              const Icon = item.Icon;
              return (
                <button
                  key={item.to}
                  onClick={() => navigate(item.to)}
                  data-testid={`${inOrg ? 'business' : 'personal'}-nav-${item.to.replace(/\//g, '-')}`}
                  className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors text-left ${
                    active ? accentActive : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                  }`}
                >
                  <Icon size={18} weight={active ? 'fill' : 'regular'} aria-hidden="true" />
                  <span className="truncate">{item.label}</span>
                </button>
              );
            })}
          </nav>

          {!inOrg && !isPro && (
            <div
              className="mt-6 rounded-xl p-3 bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-100"
              data-testid="personal-sidebar-pro-upsell"
            >
              <p className="text-xs font-semibold text-blue-900 mb-2">🚀 Unlock Pro</p>
              <ul className="text-[11px] text-blue-900/80 space-y-1 mb-3 leading-snug">
                <li>✨ AI Daily Brief</li>
                <li>🎙️ Voice task entry</li>
                <li>📎 File attachments</li>
                <li>♾️ Unlimited tasks</li>
              </ul>
              <button
                onClick={() => navigate('/settings/subscription')}
                data-testid="personal-sidebar-upgrade-btn"
                className="w-full text-xs bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-medium py-1.5 rounded-md hover:from-blue-700 hover:to-indigo-700 transition-all"
              >
                Try 14 days free
              </button>
            </div>
          )}

          <div className="mt-auto pt-6 px-1 space-y-3">
            <button
              type="button"
              onClick={() => {
                if (window.confirm(t('signOutConfirm') && t('signOutConfirm') !== 'signOutConfirm' ? t('signOutConfirm') : 'Are you sure you want to sign out?')) {
                  localStorage.removeItem('token');
                  localStorage.removeItem('user');
                  window.location.href = '/login';
                }
              }}
              data-testid="desktop-sidebar-signout"
              className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-red-600 hover:bg-red-50 transition-colors border border-red-100"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              {t('signOut') && t('signOut') !== 'signOut' ? t('signOut') : 'Sign Out'}
            </button>
            <p className="text-xs text-gray-400 truncate">Signed in as {user?.name || user?.email}</p>
          </div>
        </aside>

        {/* ---- Main column ---- */}
        <div className="flex-1 min-w-0 lg:bg-gray-50">
          {/* Mobile gradient header (hidden on desktop) */}
          {showHeader && (
            <div className="mobile-header bg-gradient-to-r from-blue-600 to-indigo-700 text-white lg:hidden">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center">
                  {showBack && (
                    <button
                      onClick={handleBack}
                      className="text-white hover:text-gray-200 transition-colors p-1 mr-3"
                    >
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                      </svg>
                    </button>
                  )}
                  <div className="flex items-center">
                    <img
                      src="/logos/edotask-wordmark.svg"
                      alt="Edotask"
                      className="h-8 mr-3"
                      style={{filter: 'brightness(0) invert(1)'}}
                    />
                    <h1 className="text-xl font-bold">{title}</h1>
                  </div>
                </div>
                {rightAction && (
                  <div className="flex items-center">
                    {rightAction}
                  </div>
                )}
              </div>
              {subtitle && <div className="text-blue-100 opacity-90">{subtitle}</div>}
            </div>
          )}

          {/* Desktop page header (replaces the gradient header at lg+) */}
          <div className="hidden lg:block px-8 pt-8 pb-4 border-b border-gray-200 bg-white">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <h1 className="text-2xl font-bold text-gray-900">{title}</h1>
                {subtitle && <div className="text-sm text-gray-500 mt-1">{subtitle}</div>}
              </div>
              <div className="flex items-center gap-3 flex-shrink-0">
                {rightAction}
                <NotificationBell />
                <div data-testid="desktop-header-switcher">
                  <WorkspaceSwitcher />
                </div>
              </div>
            </div>
          </div>

          <div className="mobile-content lg:p-8">
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MobileLayout;
